import { Crown, ExternalLink, Loader2, Check, Pause, Play } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUserSubscription } from "@/hooks/useUserSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const SubscriptionCard = () => {
  const { data: subscription, isLoading } = useUserSubscription();
  const queryClient = useQueryClient();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [showPauseOptions, setShowPauseOptions] = useState(false);
  const [pauseMonths, setPauseMonths] = useState<string>("1");

  // Fetch package name for tier display
  const { data: packageInfo } = useQuery({
    queryKey: ["subscription-package", subscription?.package_id],
    queryFn: async () => {
      if (!subscription?.package_id) return null;
      const { data, error } = await supabase
        .from("campaign_packages")
        .select("name, tier_slug, subscription_credits_monthly")
        .eq("id", subscription.package_id)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!subscription?.package_id,
  });

  // Fetch tier perks from partner codes
  const { data: tierPerks } = useQuery({
    queryKey: ["tier-perks", packageInfo?.tier_slug],
    queryFn: async () => {
      if (!packageInfo?.tier_slug) return [];
      const { data, error } = await supabase
        .from("partner_codes")
        .select("code, description, discount_type, discount_percentage, applies_to_tags, max_uses_per_month")
        .eq("tier_slug", packageInfo.tier_slug)
        .eq("is_active", true);
      if (error) return [];
      return data;
    },
    enabled: !!packageInfo?.tier_slug,
  });

  if (isLoading || !subscription) return null;

  const isActive = subscription.status === "active" || subscription.status === "trialing";
  const isCanceled = subscription.status === "canceled";
  const isPaused = !!subscription.paused_until && new Date(subscription.paused_until) > new Date();

  if (!isActive && !isCanceled) return null;

  const handleManage = async () => {
    setIsRedirecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-subscription");
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Error opening subscription portal:", err);
    } finally {
      setIsRedirecting(false);
    }
  };

  const handlePause = async () => {
    setIsPausing(true);
    try {
      const { data, error } = await supabase.functions.invoke("pause-subscription", {
        body: { action: "pause", pause_months: parseInt(pauseMonths) },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Membership paused for ${pauseMonths} month${parseInt(pauseMonths) > 1 ? "s" : ""}`);
      setShowPauseOptions(false);
      queryClient.invalidateQueries({ queryKey: ["user-subscription"] });
    } catch (err: any) {
      console.error("Error pausing subscription:", err);
      toast.error(err.message || "Failed to pause membership");
    } finally {
      setIsPausing(false);
    }
  };

  const handleResume = async () => {
    setIsResuming(true);
    try {
      const { data, error } = await supabase.functions.invoke("pause-subscription", {
        body: { action: "resume" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Membership resumed!");
      queryClient.invalidateQueries({ queryKey: ["user-subscription"] });
    } catch (err: any) {
      console.error("Error resuming subscription:", err);
      toast.error(err.message || "Failed to resume membership");
    } finally {
      setIsResuming(false);
    }
  };

  const statusLabel = isPaused ? "Paused" :
                      subscription.status === "trialing" ? "Trial" : 
                      subscription.status === "active" ? "Active" :
                      subscription.status === "canceled" ? "Canceled" : subscription.status;

  const statusVariant = isPaused ? "secondary" : isActive ? "default" : "secondary";

  const formatPerk = (perk: typeof tierPerks extends (infer T)[] ? T : never) => {
    if (!perk) return "";
    const tags = perk.applies_to_tags?.join(", ") || "all events";
    if (perk.discount_percentage === 100) {
      const limit = perk.max_uses_per_month ? `${perk.max_uses_per_month}/month` : "";
      return `${limit ? limit + " free" : "Free"} ${tags}`;
    }
    return `${perk.discount_percentage}% off ${tags}`;
  };

  return (
    <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 border-2 border-primary/30 rounded-lg p-5 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crown className="text-primary h-5 w-5" />
          <h2 className="text-lg font-semibold text-foreground">{packageInfo?.name || "Subscription"}</h2>
        </div>
        <Badge variant={statusVariant}>{statusLabel}</Badge>
      </div>

      <div className="space-y-2 text-sm">
        {isPaused && subscription.paused_until && (
          <p className="text-muted-foreground">
            Paused until: <span className="font-semibold text-foreground">
              {format(new Date(subscription.paused_until), "MMMM dd, yyyy")}
            </span>
          </p>
        )}
        {subscription.trial_ends_at && subscription.status === "trialing" && !isPaused && (
          <p className="text-muted-foreground">
            Trial ends: <span className="font-semibold text-foreground">
              {format(new Date(subscription.trial_ends_at), "MMMM dd, yyyy")}
            </span>
          </p>
        )}
        {subscription.current_period_end && isActive && !isPaused && (
          <p className="text-muted-foreground">
            Next billing: <span className="font-semibold text-foreground">
              {format(new Date(subscription.current_period_end), "MMMM dd, yyyy")}
            </span>
          </p>
        )}
        {subscription.canceled_at && (
          <p className="text-muted-foreground">
            Canceled on: <span className="font-semibold text-foreground">
              {format(new Date(subscription.canceled_at), "MMMM dd, yyyy")}
            </span>
          </p>
        )}
      </div>

      {/* Membership Perks */}
      {isActive && !isPaused && (
        <div className="mt-4 pt-3 border-t border-primary/20">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Your Perks</p>
          <ul className="space-y-1.5">
            {packageInfo?.subscription_credits_monthly && packageInfo.subscription_credits_monthly > 0 && (
              <li className="flex items-start gap-2 text-sm text-foreground">
                <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>{packageInfo.subscription_credits_monthly} credits per month</span>
              </li>
            )}
            {tierPerks?.map((perk) => (
              <li key={perk.code} className="flex items-start gap-2 text-sm text-foreground">
                <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>{perk.description || formatPerk(perk)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isActive && (
        <div className="mt-4 space-y-2">
          {/* Pause / Resume */}
          {isPaused ? (
            <Button
              variant="default"
              size="sm"
              className="w-full"
              onClick={handleResume}
              disabled={isResuming}
            >
              {isResuming ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Resume Membership
            </Button>
          ) : (
            <>
              {showPauseOptions ? (
                <div className="flex gap-2">
                  <Select value={pauseMonths} onValueChange={setPauseMonths}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 month</SelectItem>
                      <SelectItem value="2">2 months</SelectItem>
                      <SelectItem value="3">3 months</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handlePause}
                    disabled={isPausing}
                  >
                    {isPausing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPauseOptions(false)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowPauseOptions(true)}
                >
                  <Pause className="h-4 w-4 mr-2" />
                  Pause Membership
                </Button>
              )}
            </>
          )}

          {/* Manage via Stripe Portal */}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleManage}
            disabled={isRedirecting}
          >
            {isRedirecting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ExternalLink className="h-4 w-4 mr-2" />
            )}
            Manage Subscription
          </Button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
